import { useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Chip, Tooltip,
} from '@mui/material';
import { App } from 'BMapsSrc/BMapsApp';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { SidePanelContainer } from '../../info_display/SidePanel';
import { ErrorBoundary } from '../../PageWrapper';
import useEventBrokerSubscription from '../../common/helpers/useEventBrokerSubscription';
import { WorkflowEvents, WORKFLOW_PAGE_ID } from '../WorkflowPane/constants';
import { ResultsPaneTitle, ResultsPaneEvents, RESULTS_PAGE_ID } from './constants';
import {
    rankCompounds, generateFor, generateGroundedFor, pathsNotTaken,
    getActiveWeights,
} from '../../../gibbs/results';
import { getCompoundData } from '../../../gibbs/text-to-action/serializeWorkspace';
import { getFrameEmphasis } from '../../../gibbs/question-chooser';
import ResultsTable from './ResultsTable';
import CompoundDrilldown from './CompoundDrilldown';
import RankerWeightsControl from './RankerWeightsControl';

const AUTO_OPEN_PAGES = new Set([WORKFLOW_PAGE_ID, RESULTS_PAGE_ID]);

export default function ResultsPane({
    open, setOpen, handleClose,
}) {
    const [ranked, setRanked] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [activeRightPageId, setActiveRightPageId] = useState(null);
    // Per-compound grounded rationale state. Keyed by compoundId so
    // switching the selected row preserves any in-flight or
    // completed grounded request. Lifecycle is local to the
    // ResultsPane: when a new workflow completes, the cache is
    // cleared via setRanked → state implicitly resets.
    const [groundedByCompoundId, setGroundedByCompoundId] = useState({});
    const [groundedLoadingIds, setGroundedLoadingIds] = useState(new Set());

    const handleSetRightPanel = useCallback((_evt, data) => {
        setActiveRightPageId(data?.pageId || null);
    }, []);

    useEventBrokerSubscription('setrightPanel', handleSetRightPanel);

    const handleWorkflowComplete = useCallback((_evt, data) => {
        const workspace = App.Workspace;
        if (!workspace) return;

        const compoundData = getCompoundData(workspace);
        // gibbs-ranker-weights-ui: read workspace-scoped weights so
        // user overrides take effect on freshly-completed workflows.
        const rankedList = rankCompounds(
            compoundData, getActiveWeights(workspace)
        );

        setRanked(rankedList);
        setSelectedId(rankedList.length > 0 ? rankedList[0].compoundId : null);

        // New workflow → drop any cached grounded rationales so the
        // user doesn't see prose grounded on stale numeric data.
        setGroundedByCompoundId({});
        setGroundedLoadingIds(new Set());

        const completedPlan = data && data.plan ? data.plan : null;
        try {
            setSuggestions(pathsNotTaken(completedPlan, workspace) || []);
        } catch (_err) {
            // pathsNotTaken is best-effort; an upstream registry hiccup
            // shouldn't break the rest of the pane.
            setSuggestions([]);
        }

        // R5 heuristic: only auto-open if the user is already on
        // WorkflowPane or ResultsPane. If they navigated elsewhere
        // (e.g., HelpPanel, KnowledgeDashboard), respect that and
        // surface the result via the ChatBot ResultCard instead.
        const onAutoOpenPage = open || AUTO_OPEN_PAGES.has(activeRightPageId);
        if (rankedList.length > 0 && onAutoOpenPage) {
            setOpen(true);
        }
    }, [setOpen, open, activeRightPageId]);

    useEventBrokerSubscription(WorkflowEvents.COMPLETE, handleWorkflowComplete);

    // When the pane is opened (e.g. via the Results rail button) without a
    // ranked list — the workflow may have completed while the pane was
    // closed, or the user docked compounds manually outside a Gibbs
    // workflow — derive the current ranking from the workspace so the pane
    // is never blank when compounds exist.
    useEffect(() => {
        if (!open || ranked.length > 0) return;
        const workspace = App.Workspace;
        if (!workspace) return;
        const rankedList = rankCompounds(
            getCompoundData(workspace), getActiveWeights(workspace)
        );
        if (rankedList.length > 0) {
            setRanked(rankedList);
            setSelectedId(rankedList[0].compoundId);
        }
    }, [open, ranked.length]);

    const handleOpenCompound = useCallback((_evt, data) => {
        if (data && data.compoundId != null) {
            setSelectedId(data.compoundId);
            setOpen(true);
        }
    }, [setOpen]);

    useEventBrokerSubscription(ResultsPaneEvents.OPEN_COMPOUND, handleOpenCompound);

    const selectedCompound = ranked.find((c) => c.compoundId === selectedId) || null;

    const handleSuggestionClick = useCallback((suggestion) => {
        const seededPlan = {
            actions: [{ actionId: suggestion.actionId, params: {} }],
        };
        EventBroker.publish(WorkflowEvents.PROPOSED, { plan: seededPlan });
    }, []);

    /**
     * Trigger the LLM-grounded rationale for a specific compound.
     * gibbs-rationale-llm-grounding (Phase 3, 2026-05-04). Idempotent
     * by design: if the compound is already loading or already has a
     * grounded result, the call is a no-op so the user can't double-
     * trigger via rapid clicks.
     */
    const handleRequestGrounded = useCallback(async (compoundId) => {
        if (groundedByCompoundId[compoundId]) return;
        if (groundedLoadingIds.has(compoundId)) return;
        const compound = ranked.find((c) => c.compoundId === compoundId);
        if (!compound) return;

        setGroundedLoadingIds((prev) => {
            const next = new Set(prev);
            next.add(compoundId);
            return next;
        });

        try {
            const result = await generateGroundedFor(compound, { rank: compound.rank }, {});
            setGroundedByCompoundId((prev) => ({ ...prev, [compoundId]: result }));
        } catch (err) {
            // generateGroundedFor never throws by design — the
            // fallback path returns a valid result with
            // groundedFallback: true. If something extraordinary
            // happens, log + drop the loading state.
            // eslint-disable-next-line no-console
            console.error('generateGroundedFor unexpectedly threw', err);
        } finally {
            setGroundedLoadingIds((prev) => {
                const next = new Set(prev);
                next.delete(compoundId);
                return next;
            });
        }
    }, [ranked, groundedByCompoundId, groundedLoadingIds]);

    // gibbs-ranker-weights-ui: re-rank in place when the user changes
    // ranker weights via the control. Pulls fresh compound data from
    // the workspace (no stale snapshot) and re-applies rankCompounds
    // with the new active weights.
    const handleWeightsChange = useCallback(() => {
        const workspace = App.Workspace;
        if (!workspace) return;
        const compoundData = getCompoundData(workspace);
        const reranked = rankCompounds(compoundData, getActiveWeights(workspace));
        setRanked(reranked);
        // Preserve the selected compound if it's still in the new
        // ranking; otherwise reset to the top.
        const stillSelected = reranked.find((c) => c.compoundId === selectedId);
        if (!stillSelected) {
            setSelectedId(reranked.length > 0 ? reranked[0].compoundId : null);
        }
    }, [selectedId]);

    return (
        <SidePanelContainer
            title={ResultsPaneTitle}
            open={open}
            handleClose={handleClose}
            longLived
        >
            <ErrorBoundary surfaceName="Results">
            <Box sx={{
                display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
            }}
            >
                {ranked.length === 0 ? (
                    <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            No results yet. Complete a scoring workflow to see ranked compounds.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ px: 1, pt: 0.5, display: 'block' }}
                            >
                                {`${ranked.length} ranked compound${ranked.length === 1 ? '' : 's'}`}
                            </Typography>
                            {/* gibbs-question-chooser v2: when a frame is
                                active, surface a one-line banner above
                                the table. Frame-aware column ordering
                                comes through the ResultsTable props
                                below. */}
                            <FrameBanner />
                            <RankerWeightsControl
                                workspace={App.Workspace}
                                activeWeights={getActiveWeights(App.Workspace)}
                                onWeightsChange={handleWeightsChange}
                            />
                            <FrameAwareResultsTable
                                ranked={ranked}
                                selectedId={selectedId}
                                onSelect={setSelectedId}
                            />
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            <CompoundDrilldown
                                compound={selectedCompound}
                                rationale={selectedCompound ? generateFor(selectedCompound) : null}
                                groundedRationale={selectedCompound
                                    ? groundedByCompoundId[selectedCompound.compoundId]
                                    : null}
                                isGroundedLoading={selectedCompound
                                    ? groundedLoadingIds.has(selectedCompound.compoundId)
                                    : false}
                                onRequestGrounded={handleRequestGrounded}
                            />
                        </Box>
                        {suggestions.length > 0 && (
                            <Box sx={{
                                p: 1, borderTop: '1px solid', borderColor: 'divider',
                            }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                    Paths not taken
                                </Typography>
                                <Box sx={{
                                    display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5,
                                }}
                                >
                                    {suggestions.map((s) => (
                                        <Tooltip key={s.actionId} title={`${s.category} — ${s.rationale}`}>
                                            <Chip
                                                size="small"
                                                label={s.actionId}
                                                onClick={() => handleSuggestionClick(s)}
                                                clickable
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>
            </ErrorBoundary>
        </SidePanelContainer>
    );
}

/**
 * Frame-aware banner above the ResultsTable. Reads the active frame
 * from `getFrameEmphasis()` each render — cheap (pref cache lookup),
 * so we don't bother with explicit subscriptions to frame-change
 * events. Worst case: the banner is briefly stale after a frame
 * switch until the next ResultsPane re-render fires.
 */
function FrameBanner() {
    const emphasis = getFrameEmphasis();
    if (!emphasis.banner) return null;
    return (
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                px: 1,
                py: 0.5,
                backgroundColor: 'rgba(63, 81, 181, 0.08)',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                fontStyle: 'italic',
            }}
        >
            {emphasis.banner}
        </Typography>
    );
}

/**
 * ResultsTable wrapped to read frame-emphasis on render. Same
 * staleness consideration as FrameBanner — if the active frame
 * changes while ResultsPane is open, the next render picks it up.
 */
function FrameAwareResultsTable(props) {
    const emphasis = getFrameEmphasis();
    return (
        <ResultsTable
            {...props}
            columnOrder={emphasis.columnOrder}
            primaryEmphasis={emphasis.primaryEmphasis}
        />
    );
}
